/**
 * The step-up dialog's states ([F-033]), as a reducer so the transitions are
 * tested without a browser.
 *
 *   closed --open--> request --sent--> code --elevated--> closed
 *                       \--already--> closed (the session was elevated meanwhile)
 *   any --cancel--> closed;  request|code --failed--> same step with the message
 *
 * `busy` covers the one request in flight; the code step keeps the code typed
 * so far when a confirmation is refused, so a typo is corrected in place.
 */

export type ElevationStep = "closed" | "request" | "code";

export interface ElevationState {
  step: ElevationStep;
  busy: boolean;
  error: string | null;
  /** A code has been sent in this dialog at least once. */
  sent: boolean;
}

export type ElevationEvent =
  | { type: "open" }
  | { type: "submit" }
  | { type: "sent" }
  | { type: "already" }
  | { type: "elevated" }
  | { type: "failed"; message: string }
  | { type: "cancel" };

export const CLOSED: ElevationState = { step: "closed", busy: false, error: null, sent: false };

export function elevationReducer(state: ElevationState, event: ElevationEvent): ElevationState {
  switch (event.type) {
    case "open":
      // A second refusal while the dialog is open joins it; it does not restart it.
      return state.step === "closed" ? { step: "request", busy: false, error: null, sent: false } : state;
    case "submit":
      return state.step === "closed" ? state : { ...state, busy: true, error: null };
    case "sent":
      return state.step === "closed" ? state : { step: "code", busy: false, error: null, sent: true };
    case "already":
    case "elevated":
    case "cancel":
      return CLOSED;
    case "failed":
      return state.step === "closed" ? state : { ...state, busy: false, error: event.message };
  }
}
